import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, InputGroup, Modal } from "react-bootstrap";
import { Icon } from "@iconify/react";
import Select from "react-select";
import toast from "react-hot-toast";
import { isErpApiSuccess } from "../erpClient";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import {
  LockIconGrey,
  LockIconSizeOnButton,
  LockIconSizeTable,
  LockIconWhiteOnButton,
} from "../utils/LockIconforPermission";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import Pagination from "./Pagination";
import { hrmsApi } from "../utils/hrmsApi";
import { hrmsErrorToast } from "../utils/hrmsErrorToast";
import { hrmsSuccessMessage } from "../utils/hrmsSuccessMessage";
import { formatInr, formatMonthLabel } from "../utils/hrmsMoney";
import { HrmsBranchSelect } from "./HrmsBranchSelect";
import { useHrmsEmployees } from "../hooks/useHrmsEmployees";

const INCENTIVE_LOCK_ICON = "solar:lock-bold";

const INCENTIVE_NO_ACCESS_MSG =
  "You do not have permission to view or edit incentives.";

const INCENTIVE_VIEW_ONLY_MSG = "You don't have permission to view incentives.";

function IncentiveNoAccessMessage() {
  return (
    <span className="d-inline-flex align-items-center justify-content-center gap-2 flex-wrap">
      <Icon
        icon={INCENTIVE_LOCK_ICON}
        width={LockIconSizeTable}
        height={LockIconSizeTable}
        className="flex-shrink-0"
        style={{ color: LockIconGrey }}
        aria-hidden
      />
      <span>{INCENTIVE_NO_ACCESS_MSG}</span>
    </span>
  );
}

function IncentiveViewLockedMessage() {
  return (
    <span className="d-inline-flex align-items-center justify-content-center gap-2 flex-wrap">
      <Icon
        icon={INCENTIVE_LOCK_ICON}
        width={LockIconSizeTable}
        height={LockIconSizeTable}
        className="flex-shrink-0"
        style={{ color: LockIconGrey }}
        aria-hidden
      />
      <span>{INCENTIVE_VIEW_ONLY_MSG}</span>
    </span>
  );
}

function IncentiveEditLockedHint({ title }) {
  return (
    <span
      className="d-inline-flex align-items-center"
      title={title || "No permission to edit incentives (HRMS_INCENTIVE_EDIT)."}
      role="img"
      aria-label="No permission to edit incentives"
    >
      <Icon
        icon={INCENTIVE_LOCK_ICON}
        width={LockIconSizeTable}
        height={LockIconSizeTable}
        className="flex-shrink-0"
        style={{ color: LockIconGrey }}
        aria-hidden
      />
    </span>
  );
}

function IncentiveLockOnButton({ onSolidButton = false }) {
  return (
    <Icon
      icon={INCENTIVE_LOCK_ICON}
      className="me-1"
      width={LockIconSizeOnButton}
      height={LockIconSizeOnButton}
      style={{
        color: onSolidButton ? LockIconWhiteOnButton : LockIconGrey,
      }}
      aria-hidden
    />
  );
}

const VALUE_MODES = [
  { value: "PERCENTAGE", label: "%" },
  { value: "AMOUNT", label: "Amount" },
];

function rowEmployeeId(r) {
  const id = Number(r.employee_id ?? r.user_admin_id);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function extractIncentiveList(res) {
  const d = res?.data;
  if (Array.isArray(d?.data)) return d.data;
  if (Array.isArray(d?.incentives)) return d.incentives;
  if (Array.isArray(d)) return d;
  return [];
}

function normalizeValueType(raw) {
  const u = String(raw || "").toUpperCase();
  if (u === "PERCENTAGE" || u === "PERCENT") return "PERCENTAGE";
  return "AMOUNT";
}

export function IncentivesTab({
  branchId,
  onBranchChange,
  branchOptions,
  branchesLoading,
}) {
  const { buttonClass, tableClass, cardClass, buttonTableStyle } =
    useButtonTableStyle();
  const canView = hasPermission(PERMISSIONS.HRMS_INCENTIVE_VIEW);
  const canEdit = hasPermission(PERMISSIONS.HRMS_INCENTIVE_EDIT);
  const hasAnyAccess = canView || canEdit;
  const editLockedTitle =
    "No permission to edit incentives (HRMS_INCENTIVE_EDIT).";

  const [searchQuery, setSearchQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [loading, setLoading] = useState(false);

  const [formBranchId, setFormBranchId] = useState(null);
  const { selectOptions: formEmpOptions, loading: formEmpLoading } =
    useHrmsEmployees(formBranchId);

  useEffect(() => {
    setRows([]);
  }, [branchId]);

  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formEmp, setFormEmp] = useState(null);
  const [formMonth, setFormMonth] = useState(() =>
    new Date().toISOString().slice(0, 7),
  );
  const [formValueMode, setFormValueMode] = useState("AMOUNT");
  const [formValue, setFormValue] = useState("");
  const [formNote, setFormNote] = useState("");
  const formRemarks = formNote;
  const setFormRemarks = setFormNote;

  const selectStyles = useMemo(
    () => ({
      control: (p) => ({
        ...p,
        minHeight: 44,
        minWidth: 200,
        borderRadius: buttonTableStyle === "square" ? 0 : 8,
        borderColor: "#e0e6eb",
        boxShadow: "none",
      }),
      menu: (p) => ({ ...p, zIndex: 9999 }),
    }),
    [buttonTableStyle],
  );

  const fetchList = useCallback(async () => {
    if (!branchId || !hasAnyAccess || !canView) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const body = {
        branch_id: Number(branchId),
        pagination: { page: 1, limit: 500 },
      };
      const res = await hrmsApi("incentive_list", body);
      if (isErpApiSuccess(res) && res.data) {
        setRows(extractIncentiveList(res));
      } else {
        setRows([]);
        if (res?.success === false && res?.message) toast.error(res.message);
      }
    } catch (err) {
      hrmsErrorToast(err, "Could not load incentives.");
    } finally {
      setLoading(false);
    }
  }, [branchId, canView, hasAnyAccess]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  useEffect(() => {
    setCurrentPage(1);
  }, [branchId, searchQuery]);

  const branchLabelById = useMemo(() => {
    const map = new Map();
    for (const b of branchOptions) {
      map.set(Number(b.value), String(b.label || ""));
    }
    return map;
  }, [branchOptions]);

  const formatIncentiveValue = useCallback((r) => {
    const vt = normalizeValueType(r.value_type);
    const val = Number(r.value);
    if (vt === "PERCENTAGE" && Number.isFinite(val)) {
      const pct = `${val}%`;
      const computed = Number(r.computed_amount);
      if (Number.isFinite(computed)) {
        return `${pct} (${formatInr(computed)})`;
      }
      return pct;
    }
    const amount = r.computed_amount ?? r.value ?? r.amount;
    return formatInr(amount);
  }, []);

  const rowSearchText = useCallback(
    (r) => {
      const monthRaw = String(r.month || r.incentive_month || "").trim();
      const monthLabel = formatMonthLabel(monthRaw);
      const branchLabel =
        branchLabelById.get(Number(r.branch_id)) ||
        String(r.branch_name || "").trim();
      const yearMatch = monthRaw.match(/(\d{4})/);
      const monthNumMatch = monthRaw.match(/-(\d{2})/);
      let monthName = "";
      if (yearMatch && monthNumMatch) {
        const d = new Date(
          Number(yearMatch[1]),
          Number(monthNumMatch[1]) - 1,
          1,
        );
        monthName = d.toLocaleString("en-IN", { month: "long" });
      }
      return [
        r.user_name,
        monthLabel,
        monthRaw,
        monthName,
        yearMatch ? yearMatch[1] : "",
        branchLabel,
        r.note,
        r.remarks,
        r.value_type,
        String(r.value ?? ""),
        String(r.computed_amount ?? ""),
        formatIncentiveValue(r),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
    },
    [branchLabelById, formatIncentiveValue],
  );

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => rowSearchText(r).includes(q));
  }, [rows, searchQuery, rowSearchText]);

  const displayRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredRows.slice(start, start + itemsPerPage);
  }, [filteredRows, currentPage, itemsPerPage]);

  const resetFormFields = () => {
    setFormEmp(null);
    setFormMonth(new Date().toISOString().slice(0, 7));
    setFormValueMode("AMOUNT");
    setFormValue("");
    setFormNote("");
  };

  const openAdd = () => {
    if (!canEdit) return;
    setEditingId(null);
    setFormBranchId(branchId);
    resetFormFields();
    setShowForm(true);
  };

  const openEdit = (r) => {
    if (!canEdit) return;
    setEditingId(r.id);
    const rowBranch = Number(r.branch_id ?? branchId);
    setFormBranchId(
      Number.isFinite(rowBranch) && rowBranch > 0 ? rowBranch : branchId,
    );
    const empId = rowEmployeeId(r);
    const opt = formEmpOptions.find((o) => Number(o.value) === empId);
    setFormEmp(
      opt || {
        value: empId,
        label:
          String(r.user_name || r.employee_name || "").trim() ||
          `User #${empId}`,
      },
    );
    setFormMonth(
      String(r.incentive_month || r.month || "")
        .trim()
        .slice(0, 7),
    );
    const vt = normalizeValueType(r.value_type);
    setFormValueMode(vt);
    if (vt === "PERCENTAGE") {
      setFormValue(String(r.value ?? ""));
    } else {
      setFormValue(String(r.value ?? r.computed_amount ?? r.amount ?? ""));
    }
    setFormNote(String(r.note ?? r.remarks ?? ""));
    setShowForm(true);
  };

  const onFormBranchChange = (id) => {
    setFormBranchId(id);
    setFormEmp(null);
  };

  const closeForm = () => {
    if (saving) return;
    setShowForm(false);
    setEditingId(null);
    setFormBranchId(null);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!canEdit || !formBranchId) {
      toast.error("You do not have permission to edit incentives.");
      return;
    }
    if (!formEmp?.value) {
      toast.error("Select employee.");
      return;
    }
    if (!/^\d{4}-\d{2}$/.test(String(formMonth).trim())) {
      toast.error("Invalid month.");
      return;
    }
    const raw = String(formValue || "")
      .replace(/,/g, "")
      .trim();
    const num = Number(raw);
    if (!raw || !Number.isFinite(num) || num < 0) {
      toast.error(
        formValueMode === "PERCENTAGE"
          ? "Enter a valid percentage."
          : "Enter a valid amount.",
      );
      return;
    }
    if (formValueMode === "PERCENTAGE" && num > 100) {
      toast.error("Percentage cannot exceed 100.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        branch_id: Number(formBranchId),
        employee_id: Number(formEmp.value),
        month: String(formMonth).trim(),
        value_type: formValueMode === "PERCENTAGE" ? "PERCENTAGE" : "AMOUNT",
        value: num,
        note: String(formNote || "").trim(),
      };
      if (editingId != null) body.id = Number(editingId);
      const res = await hrmsApi("incentive_save", body);
      if (isErpApiSuccess(res)) {
        toast.success(
          hrmsSuccessMessage(
            res,
            editingId != null ? "Incentive updated." : "Incentive added.",
          ),
        );
        closeForm();
        await fetchList();
      } else {
        toast.error(res?.message || "Could not save.");
      }
    } catch (err) {
      hrmsErrorToast(err, "Could not save incentive.");
    } finally {
      setSaving(false);
    }
  };

  const showActionsColumn = hasAnyAccess;
  const colCount = showActionsColumn ? 5 : 4;

  return (
    <div className="row g-3">
      <div className="col-12">
        <div className="d-flex justify-content-between align-items-center mb-3 gap-3">
          <div className="d-flex align-items-center gap-3 flex-nowrap">
            <HrmsBranchSelect
              branchId={branchId}
              onChangeBranchId={onBranchChange}
              branchOptions={branchOptions}
              loading={branchesLoading}
              buttonTableStyle={buttonTableStyle}
              disabled={!hasAnyAccess}
            />
            <input
              type="text"
              className={`form-control flex-shrink-0 ${buttonClass}`}
              style={{ width: 260 }}
              placeholder="Search..."
              value={searchQuery}
              onChange={(ev) => {
                setSearchQuery(ev.target.value);
                setCurrentPage(1);
              }}
              disabled={!hasAnyAccess || !canView}
              aria-label="Search incentives"
            />
          </div>
          {hasAnyAccess ? (
            <Button
              type="button"
              className={`btn btn-success flex-shrink-0 ${buttonClass}`}
              disabled={!canEdit || !branchId}
              title={!canEdit ? editLockedTitle : undefined}
              onClick={canEdit ? openAdd : undefined}
            >
              {!canEdit ? <IncentiveLockOnButton onSolidButton /> : null}
              Add Incentive
            </Button>
          ) : null}
        </div>
      </div>

      <div className="col-12">
        <div
          className={`table-responsive border position-relative ${cardClass} ${buttonTableStyle === "square" ? "rounded-0" : ""
            }`}
        >
          {!hasAnyAccess ? (
            <div
              className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-90"
              style={{ zIndex: 5 }}
            >
              <IncentiveNoAccessMessage />
            </div>
          ) : null}
          <table
            className={`table text-nowrap mb-0 align-middle ${tableClass} ${buttonTableStyle === "square" ? "rounded-0" : ""
              }`}
          >
            <thead className="text-dark fs-4">
              <tr>
                <th>Employee</th>
                <th>Month</th>
                <th className="text-end">Incentive</th>
                <th className="text-wrap" style={{ minWidth: "12rem" }}>
                  Note
                </th>
                {showActionsColumn ? <th>Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {!hasAnyAccess ? (
                <tr>
                  <td
                    colSpan={colCount}
                    className="text-center py-5 text-muted"
                  >
                    <IncentiveNoAccessMessage />
                  </td>
                </tr>
              ) : !canView ? (
                <tr>
                  <td
                    colSpan={colCount}
                    className="text-center py-5 text-muted"
                  >
                    <IncentiveViewLockedMessage />
                  </td>
                </tr>
              ) : !branchId ? (
                <tr>
                  <td
                    colSpan={colCount}
                    className="text-center py-5 text-muted"
                  >
                    Select a branch to view incentives.
                  </td>
                </tr>
              ) : loading ? (
                <tr>
                  <td
                    colSpan={colCount}
                    className="text-center py-5 text-muted"
                  >
                    Loading…
                  </td>
                </tr>
              ) : displayRows.length > 0 ? (
                displayRows.map((r) => (
                  <tr key={r.id}>
                    <td>{String(r.user_name || "").trim() || "—"}</td>
                    <td>{formatMonthLabel(r.incentive_month || r.month)}</td>
                    <td className="text-end">{formatIncentiveValue(r)}</td>
                    <td className="text-wrap">
                      {String(r.note ?? r.remarks ?? "—")}
                    </td>
                    {showActionsColumn ? (
                      <td>
                        {canEdit ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="primary"
                            className={buttonClass}
                            onClick={() => openEdit(r)}
                          >
                            <Icon icon="solar:pen-bold" width={16} />
                          </Button>
                        ) : (
                          <IncentiveEditLockedHint title={editLockedTitle} />
                        )}
                      </td>
                    ) : null}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={colCount}
                    className="text-center py-5 text-muted"
                  >
                    No incentives found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {hasAnyAccess && canView && filteredRows.length > 0 ? (
        <div className="col-12">
          <Pagination
            totalItems={filteredRows.length}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(n) => {
              setItemsPerPage(n);
              setCurrentPage(1);
            }}
          />
        </div>
      ) : null}

      <Modal show={showForm} onHide={closeForm} centered size="md">
        <Modal.Header closeButton={!saving}>
          <Modal.Title>
            {editingId ? "Edit incentive" : "Add incentive"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={submitForm}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Branch <span className="text-danger">*</span></Form.Label>
              <HrmsBranchSelect
                branchId={formBranchId}
                onChangeBranchId={onFormBranchChange}
                branchOptions={branchOptions}
                loading={branchesLoading}
                buttonTableStyle={buttonTableStyle}
                disabled={saving || !!editingId}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Employee <span className="text-danger">*</span></Form.Label>
              <Select
                inputId="hrms-inc-form-emp"
                options={formEmpOptions}
                value={formEmp}
                onChange={setFormEmp}
                isLoading={formEmpLoading}
                isDisabled={saving || !!editingId || !formBranchId}
                placeholder={
                  formBranchId ? "Select employee" : "Select branch first"
                }
                styles={selectStyles}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Month <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="month"
                value={formMonth}
                onChange={(ev) => setFormMonth(ev.target.value)}
                disabled={saving}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                {formValueMode === "PERCENTAGE" ? "Percentage" : "Amount"}{" "}
                <span className="text-danger">*</span>
              </Form.Label>
              <InputGroup>
                <Form.Select
                  value={formValueMode}
                  onChange={(ev) => {
                    setFormValueMode(ev.target.value);
                    setFormValue("");
                  }}
                  disabled={saving}
                  style={{ maxWidth: 120 }}
                  className={buttonTableStyle === "square" ? "rounded-0" : ""}
                  aria-label="Value type"
                >
                  {VALUE_MODES.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control
                  type="number"
                  step="0.01"
                  min={0}
                  max={formValueMode === "PERCENTAGE" ? 100 : undefined}
                  value={formValue}
                  onChange={(ev) => setFormValue(ev.target.value)}
                  disabled={saving}
                  placeholder={
                    formValueMode === "PERCENTAGE"
                      ? "Enter percentage"
                      : "Enter amount"
                  }
                  className={buttonTableStyle === "square" ? "rounded-0" : ""}
                />
              </InputGroup>
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label>Note</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formRemarks}
                onChange={(ev) => setFormRemarks(ev.target.value)}
                disabled={saving}
                placeholder="Enter Note"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              type="submit"
              variant="success"
              className={buttonClass}
              disabled={saving || !canEdit}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
